import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Globe, Users, UserCheck, AlertTriangle, Clock } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

const MODES = [
  { id: "open", label: "Open to eligible animators", desc: "Anyone meeting the role/tier eligibility can see and claim it.", icon: Globe },
  { id: "selected", label: "Selected animators only", desc: "Only the animators you pick can see or claim this job — invisible to everyone else.", icon: Users },
  { id: "direct", label: "Direct assignment", desc: "Assign straight to one animator — same claim/payment workflow, no public listing.", icon: UserCheck },
];

const FILTERS = [
  { id: "all", label: "All" },
  { id: "available", label: "Available" },
  { id: "active", label: "Has active jobs" },
  { id: "standard", label: "Standard" },
  { id: "hq", label: "High Quality" },
  { id: "due_soon", label: "Due soon" },
  { id: "overdue", label: "Overdue" },
];

const tierLabel = (roles) => {
  const hq = (roles || []).includes("High-Quality Animator");
  const std = (roles || []).includes("Standard Animator");
  if (hq && std) return "STD + HQ";
  if (hq) return "HQ";
  if (std) return "STD";
  return "—";
};

const workloadText = (a) => {
  if (a.active_count === 0) return { text: "0 active — Available", cls: "text-emerald-300" };
  const bits = [`${a.active_count} active`];
  if (a.overdue_count > 0) return { text: `${a.active_count} active — ${a.overdue_count} overdue`, cls: "text-rose-300" };
  if (a.earliest_deadline) {
    const days = Math.ceil((new Date(a.earliest_deadline + "T00:00:00") - new Date()) / 86400000);
    bits.push(days <= 0 ? "due today" : days === 1 ? "due tomorrow" : `next due ${new Date(a.earliest_deadline + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}`);
  }
  return { text: bits.join(" — "), cls: a.due_soon_count > 0 ? "text-amber-300" : "text-[var(--pv-text-3)]" };
};

// Owner-only animator targeting control for job creation/editing. Workload info is
// informational only — it never blocks picking someone who already has work.
export const AnimatorAccessPicker = ({ access, selectedIds, onChange, eligibleRoles = [], hideDirect = false }) => {
  const modes = hideDirect ? MODES.filter((m) => m.id !== "direct") : MODES;
  const [workload, setWorkload] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sortLow, setSortLow] = useState(true);

  useEffect(() => {
    if (access === "open" || workload) return;
    axios.get(`${API}/admin/animator-workload`, { headers: authHeaders() })
      .then((r) => setWorkload(r.data.animators || []))
      .catch(() => setWorkload([]));
  }, [access, workload]);

  const list = useMemo(() => {
    let rows = workload || [];
    const eligible = (a) => !eligibleRoles.length || (a.animator_roles || []).some((r) => eligibleRoles.includes(r));
    rows = rows.map((a) => ({ ...a, _eligible: eligible(a) }));
    if (filter === "available") rows = rows.filter((a) => a.active_count === 0);
    else if (filter === "active") rows = rows.filter((a) => a.active_count > 0);
    else if (filter === "standard") rows = rows.filter((a) => (a.animator_roles || []).includes("Standard Animator"));
    else if (filter === "hq") rows = rows.filter((a) => (a.animator_roles || []).includes("High-Quality Animator"));
    else if (filter === "due_soon") rows = rows.filter((a) => a.due_soon_count > 0);
    else if (filter === "overdue") rows = rows.filter((a) => a.overdue_count > 0);
    if (sortLow) rows = [...rows].sort((x, y) => x.active_count - y.active_count || (x.earliest_deadline || "9999").localeCompare(y.earliest_deadline || "9999"));
    return rows;
  }, [workload, filter, sortLow, eligibleRoles]);

  const toggle = (id) => {
    if (access === "direct") onChange(access, [id]);
    else onChange(access, selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <div data-testid="animator-access-picker" className="sm:col-span-2 rounded-2xl border border-cyan-500/20 bg-[var(--pv-subtle)] p-4">
      <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Animator access <span className="font-normal text-[var(--pv-text-4)]">— owner only; workers never see who else was picked</span></p>
      <div className="grid gap-2 sm:grid-cols-3">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`access-mode-${m.id}`}
            onClick={() => onChange(m.id, m.id === "open" ? [] : selectedIds)}
            className={`rounded-2xl border p-3.5 text-left transition-colors ${access === m.id ? "border-cyan-400 bg-cyan-500/10" : "border-[var(--pv-border)] bg-[var(--pv-card)] hover:border-cyan-500/30"}`}
          >
            <p className={`flex items-center gap-1.5 text-xs font-bold ${access === m.id ? "text-cyan-300" : "text-[var(--pv-text-2)]"}`}>
              <m.icon className="h-3.5 w-3.5" /> {m.label}
            </p>
            <p className="mt-1 text-[10px] leading-relaxed text-[var(--pv-text-4)]">{m.desc}</p>
          </button>
        ))}
      </div>

      {access !== "open" && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                data-testid={`picker-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-3 py-1 text-[10px] font-bold transition-colors ${filter === f.id ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)] hover:text-cyan-300"}`}
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              data-testid="picker-sort-workload"
              onClick={() => setSortLow((s) => !s)}
              className={`ml-auto rounded-full px-3 py-1 text-[10px] font-bold transition-colors ${sortLow ? "bg-cyan-500/15 border border-cyan-500/30 text-cyan-300" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)]"}`}
            >
              Lowest workload first
            </button>
          </div>

          <div data-testid="picker-animator-list" className="mt-3 max-h-64 space-y-1.5 overflow-y-auto pr-1">
            {!workload ? (
              <p className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-cyan-400" /></p>
            ) : list.length === 0 ? (
              <p data-testid="picker-empty" className="rounded-xl border border-dashed border-[var(--pv-border)] py-6 text-center text-xs text-[var(--pv-text-4)]">No animators match this filter.</p>
            ) : (
              list.map((a) => {
                const wl = workloadText(a);
                const checked = selectedIds.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`picker-animator-${a.id}`}
                    disabled={!a._eligible}
                    onClick={() => toggle(a.id)}
                    title={a._eligible ? undefined : "Current eligibility doesn't match this job's role/tier"}
                    className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-3.5 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                      checked ? "border-cyan-400 bg-cyan-500/10" : "border-[var(--pv-border)] bg-[var(--pv-card)] hover:border-cyan-500/30"
                    }`}
                  >
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-${access === "direct" ? "full" : "md"} border ${checked ? "border-cyan-400 bg-cyan-500 text-white" : "border-[var(--pv-border-strong)]"}`}>
                      {checked && <UserCheck className="h-3 w-3" />}
                    </span>
                    <span className="text-xs font-bold text-[var(--pv-text)]">{a.name}</span>
                    <span className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-raised)] px-2 py-0.5 text-[9px] font-bold text-[var(--pv-text-2)]">{tierLabel(a.animator_roles)}</span>
                    <span data-testid={`picker-workload-${a.id}`} className={`flex items-center gap-1 text-[11px] font-semibold ${wl.cls}`}>
                      {a.overdue_count > 0 ? <AlertTriangle className="h-3 w-3" /> : a.active_count > 0 ? <Clock className="h-3 w-3" /> : null}
                      {wl.text}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          <p className="mt-2 text-[10px] text-[var(--pv-text-4)]">
            {access === "direct"
              ? "Pick exactly one animator — the job is assigned to them immediately at their matching tier rate (budget reserve still applies)."
              : `Pick one or more — only they will see this job. Workload is informational; you can still assign to someone busy. ${selectedIds.length ? `${selectedIds.length} selected.` : ""}`}
          </p>
        </div>
      )}
    </div>
  );
};
